<template>
  <div
    v-if="value"
    no-gutters
    class="add-share pa-4"
  >
    <div class="d-flex">
      <sn-text-field
        v-model="name"
        label="Name"
        class="flex-grow-1 mr-6"
        data-cy="share-preferences-name-field"
        :rules="nameRules"
        :validate-on-blur="validateOnBlur"
        @blur="validateOnBlur = false"
      />
      <sn-text-field
        v-model="email"
        label="Email"
        class="flex-grow-1 mr-4"
        data-cy="share-preferences-email-field"
        :rules="emailRules"
        :validate-on-blur="validateOnBlur"
        @blur="validateOnBlur = false"
      />
      <div class="d-flex align-end flex-column pr-4">
        <v-icon
          class="sn-text-primary"
          data-cy="cancel-edit-btn"
          :disabled="saving"
          @click="cancel"
        >
          $snExit
        </v-icon>
        <sn-btn
          style="marginTop: 10px"
          text="Save"
          type="secondary"
          size="large"
          data-cy="share-preferences-save-btn"
          :disabled="!valid"
          :loading="saving"
          @click="save"
        />
      </div>
    </div>
    <sn-checkbox
      v-model="manager"
      class="pt-2"
      data-cy="manager-checkbox"
      :simple="true"
      label="This is my manager"
    />
    <sn-modal
      v-model="showConfirmEditModal"
      type="decision"
      title="Edit contact"
      data-cy="confirm-edit-email-modal"
      primary-text="Continue"
      warning-text="This action cannot be undone"
      warning-icon="$snWarning"
      @submit="confirmEmailEdit"
    >
      <p>Editing the email will <b>revoke access</b> to any files that you previously shared with this contact.</p>
      <p>Are you sure you want to proceed?</p>
    </sn-modal>
  </div>
</template>

<script>
import snValidationsService from '~/services/snValidationsService.js'
import closingPortalService from '~/services/snClosingPortalService'

export default {
  name: 'EditShareContact',
  props: {
    value: {
      type: Boolean,
      required: false
    },
    contactGuid: {
      type: String,
      required: false,
      default: null
    },
    sharedContactEmails: {
      type: Array,
      required: true
    },
    oldName: {
      type: String,
      default: null,
      required: false
    },
    oldEmail: {
      type: String,
      default: null,
      required: false
    },
    oldManager: {
      type: Boolean,
      default: false,
      required: false
    },
    currentlySharedFilesCount: {
      type: Number,
      default: 0,
      required: false
    }
  },
  data () {
    return {
      name: this.oldName,
      email: this.oldEmail,
      manager: this.oldManager,
      validateOnBlur: true,
      saving: false,
      showConfirmEditModal: false
    }
  },
  computed: {
    changed () {
      return this.changedName !== undefined ||
        this.changedEmail !== undefined ||
        this.changedManager !== undefined
    },
    changedName () {
      if (this.name !== this.oldName) return this.name
      return undefined
    },
    changedEmail () {
      if (this.email !== null && this.email.localeCompare(
        this.oldEmail, undefined, { sensitivity: 'base' }
      ) !== 0) {
        return this.email
      }
      return undefined
    },
    changedManager () {
      if (this.manager !== this.oldManager) return this.manager
      return undefined
    },
    emailRules () {
      const rules = []
      const invalidEmailRule = v => snValidationsService.email(v || '') || 'Enter email address'
      const duplicateShareRule = v => !this.alreadySharedWith(v) || 'Already shared with this user'
      rules.push(invalidEmailRule, duplicateShareRule)
      return rules
    },
    nameRules () {
      return [v => snValidationsService.name(v || '') || 'Enter name', v => v?.replace(/\s/g, '')?.length > 0 || 'Blank name']
    },
    valid () {
      return this.email &&
        this.changed &&
        this.name.replace(/\s/g, '').length > 0 &&
        snValidationsService.email(this.email) &&
        !this.alreadySharedWith(this.email)
    }
  },
  watch: {
    value () {
      this.reset()
    }
  },
  methods: {
    save () {
      if (!this.contactGuid) {
        this.addContact()
      } else if (!this.changedEmail || this.currentlySharedFilesCount === 0) {
        this.editContact()
      } else {
        this.showConfirmEditModal = true
      }
    },
    confirmEmailEdit () {
      this.showConfirmEditModal = false
      this.editContact()
    },
    cancel () {
      this.$emit('input', false)
    },
    reset () {
      this.name = this.oldName
      this.email = this.oldEmail
      this.manager = this.oldManager
    },
    alreadySharedWith (email) {
      // ignore already shared with own email
      if (!this.changedEmail) return false

      for (const existingEmail of this.sharedContactEmails) {
        if (existingEmail.localeCompare(
          email, undefined, { sensitivity: 'base' }
        ) === 0) {
          return true
        }
      }
      return false
    },
    addContact () {
      this.saving = true
      closingPortalService.createSharedContact(
        this.name, this.email, this.manager
      ).then(({ data: { create_closing_portal_shared_contact: response } }) => {
        this.$emit('save', response)
        this.$emit('input', false)
        this.saving = false
      }).catch(err => {
        console.error(err)
      })
    },
    editContact () {
      this.saving = true
      closingPortalService.updateClosingPortalSharedContactPreferences(
        this.contactGuid,
        undefined,
        this.changedName,
        this.changedEmail,
        this.changedManager
      ).then(({ data: { update_closing_portal_shared_contact_preferences: response } }) => {
        this.$emit('save', response)
        this.$emit('input', false)
        this.saving = false
      }).catch(err => {
        console.error(err)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui/_snColorVariables.scss';

.add-share {
  background-color: $sn-brand-01;
  width: 100%;
}
</style>
